import { TaskGanttView, TaskStatus } from '@nexelem/graphne-api';
import { GanttUtils } from '../../utils';
import { NodeType, QueueBatchNode, QueueBatchOrTaskNode, QueueMultipleNode, QueueTaskNode } from '../types';
import { useGanttSimulationTaskStateStore } from '../store';

export type NodeFlags = {
  notEqualMultinode: boolean;
  toPublish: boolean;
  frozen: boolean;
  retooling: boolean;
  paused: boolean;
  downtime: boolean;
  priority: boolean;
  warehouseShortage: boolean;
  overdue: boolean;
  comment: boolean;
};

export const useNodeFlagsMapper = (node: QueueTaskNode) => {
  const frozen = useGanttSimulationTaskStateStore((state) => state.frozenTaskIds.some((id) => id === node.id));
  const toPublish = useGanttSimulationTaskStateStore((state) => state.taskToPublishIds.some((id) => id === node.id));

  return nodeFlagsMapper(node, frozen, toPublish);
};

const nodeFlagsMapper = (node: QueueTaskNode, frozen: boolean, toPublish: boolean): NodeFlags => {
  return {
    notEqualMultinode: false,
    /** Only false, undefined is not valid */
    toPublish: node.status === TaskStatus.Pending ? (node.simulationId && toPublish === false ? false : true) : true,
    frozen: node.status === TaskStatus.Pending ? frozen : false,
    retooling: !!node.setupEnd && node.setupEnd !== node.start,
    paused: node.isPaused,
    /** Not implemented yet */
    downtime: false,
    priority: node.priority,
    warehouseShortage: (node.missingStocks?.length ?? 0) > 0,
    overdue: GanttUtils.hasOverdueError(node.errors),
    comment: GanttUtils.hasComment(node.orders),
  };
};

export const useNodeBatchFlagsMapper = (batchNode: QueueBatchNode) => {
  const firstTask = batchNode.tasks?.[0];

  const frozen = useGanttSimulationTaskStateStore((state) => state.frozenTaskIds.some((id) => id === firstTask?.id));
  const toPublish = useGanttSimulationTaskStateStore((state) =>
    state.taskToPublishIds.some((id) => id === firstTask?.id),
  );

  return nodeBatchFlagsMapper(batchNode, frozen, toPublish);
};

export const nodeBatchFlagsMapper = (batchNode: QueueBatchNode, frozen: boolean, toPublish: boolean): NodeFlags => {
  const firstTask = batchNode.tasks?.[0];

  return {
    notEqualMultinode: false,
    /** Only false, undefined is not valid */
    toPublish:
      firstTask?.status === TaskStatus.Pending ? (firstTask?.simulationId && toPublish === false ? false : true) : true,
    frozen: firstTask?.status === TaskStatus.Pending ? frozen : false,
    retooling: !!firstTask?.setupEnd && firstTask?.setupEnd !== firstTask?.start,
    paused: !!firstTask?.isPaused,
    /** Not implemented yet */
    downtime: false,
    priority: !!firstTask?.priority,
    warehouseShortage: (firstTask?.missingStocks?.length ?? 0) > 0,
    overdue: GanttUtils.hasOverdueError(firstTask?.errors),
    comment: GanttUtils.hasComment(firstTask?.orders),
  };
};

const getOverlappingTaskIds = (overlappingNodes: QueueMultipleNode['overlappingNodes']) =>
  overlappingNodes.flatMap((node) => {
    if (node.type === NodeType.TASK) {
      return [node.id];
    }

    if (node.type === NodeType.BATCH) {
      return node.tasks?.map((task) => task.id) ?? [];
    }

    return [];
  });

export const useNodeMultipleFlagsMapper = (multipleNode: QueueMultipleNode): NodeFlags => {
  const overlappingTaskIds = getOverlappingTaskIds(multipleNode.overlappingNodes);

  const frozenTasksIds = useGanttSimulationTaskStateStore((state) =>
    state.frozenTaskIds.filter((id) => overlappingTaskIds.includes(id)),
  );
  const toPublishTasksIds = useGanttSimulationTaskStateStore((state) =>
    state.taskToPublishIds.filter((id) => overlappingTaskIds.includes(id)),
  );

  const tasksFlags = multipleNode.overlappingNodes
    .filter((node): node is QueueBatchOrTaskNode => node.type === NodeType.TASK || node.type === NodeType.BATCH)
    .map((node) => {
      if (node.type === NodeType.TASK) {
        const frozen = frozenTasksIds.some((id) => id === node.id);
        const toPublish = toPublishTasksIds.some((id) => id === node.id);
        return nodeFlagsMapper(node, frozen, toPublish);
      }

      const firstTaskId = node.tasks?.[0]?.id;
      const frozen = frozenTasksIds.some((id) => id === firstTaskId);
      const toPublish = toPublishTasksIds.some((id) => id === firstTaskId);
      return nodeBatchFlagsMapper(node, frozen, toPublish);
    });

  const equality: Omit<NodeFlags, 'count' | 'notEqualMultinode'> = {
    toPublish: tasksFlags.some((flags) => tasksFlags[0].toPublish !== flags.toPublish),
    frozen: tasksFlags.some((flags) => tasksFlags[0].frozen !== flags.frozen),
    retooling: tasksFlags.some((flags) => tasksFlags[0].retooling !== flags.retooling),
    paused: tasksFlags.some((flags) => tasksFlags[0].paused !== flags.paused),
    /** Not implemented yet */
    downtime: false,
    priority: tasksFlags.some((flags) => tasksFlags[0].priority !== flags.priority),
    warehouseShortage: tasksFlags.some((flags) => tasksFlags[0].warehouseShortage !== flags.warehouseShortage),
    overdue: tasksFlags.some((flags) => tasksFlags[0].overdue !== flags.overdue),
    comment: tasksFlags.some((flags) => tasksFlags[0].comment !== flags.comment),
  };

  return {
    notEqualMultinode: Object.values(equality).some(Boolean),
    toPublish: !tasksFlags.some((flag) => flag.toPublish === false),
    frozen: tasksFlags.some((flag) => flag.frozen),
    retooling: tasksFlags.some((flag) => flag.retooling),
    paused: tasksFlags.some((flag) => flag.paused),
    /** Not implemented yet */
    downtime: false,
    priority: tasksFlags.some((flag) => flag.priority),
    warehouseShortage: tasksFlags.some((flag) => flag.warehouseShortage),
    overdue: tasksFlags.some((flag) => flag.overdue),
    comment: tasksFlags.some((flag) => flag.comment),
  };
};

export const useNodeMultiplePopperFlagsMapper = (node: TaskGanttView) => {
  const frozen = useGanttSimulationTaskStateStore((state) => state.frozenTaskIds.some((id) => id === node.id));
  const toPublish = useGanttSimulationTaskStateStore((state) => state.taskToPublishIds.some((id) => id === node.id));

  return nodeMultiplePopperFlagsMapper(node, frozen, toPublish);
};

const nodeMultiplePopperFlagsMapper = (node: TaskGanttView, frozen: boolean, toPublish: boolean): NodeFlags => {
  return {
    notEqualMultinode: false,
    retooling: !!node.setupEnd && node.setupEnd !== node.start,
    paused: node.isPaused,
    downtime: false,
    frozen: node.status === TaskStatus.Pending ? frozen : false,
    toPublish: node.status === TaskStatus.Pending ? (node.simulationId && toPublish === false ? false : true) : true,
    priority: node.priority,
    warehouseShortage: (node.missingStocks?.length ?? 0) > 0,
    overdue: GanttUtils.hasOverdueError(node.errors),
    comment: GanttUtils.hasComment(node.orders),
  };
};
